/**
 * La aritmetica y las reglas de la caja, separadas de la UI.
 *
 * POR QUE ESTE ARCHIVO EXISTE
 * ---------------------------
 * Todo lo de aca decide plata: cuanto entra del otro lado de un cambio de
 * divisa, que cuentas pueden recibir un cheque, cual cheque esta vencido. Son
 * las tres cosas que, si estan mal, no se notan hasta que la caja no cierra.
 *
 * Ninguna de estas funciones toca React ni el DOM: se pueden probar con numeros
 * sueltos, que es exactamente como hay que probar la plata.
 */
import {
  aArs,
  convertir,
  formatearCotizacionNumero,
  redondear,
  redondearCotizacion,
  type Moneda,
} from "@tora/shared";

import {
  aNumero,
  type Cheque,
  type CuentaFinanciera,
  type MovimientoCaja,
  type Numerico,
} from "@/lib/tipos";

/**
 * La cotizacion como numero pelado, sin la unidad: "1.601".
 *
 * `formatearCotizacion()` de `@tora/shared` ya le pega el sufijo "ARS/USD", que
 * en una frase como "US$ 1 = $ 1.601" queda dicho dos veces. El formato (sin
 * decimales salvo que los tenga) es el de `formatearCotizacionNumero`.
 */
export function textoCotizacion(valor: number): string {
  return formatearCotizacionNumero(valor);
}

// ─────────────────────────────────────────────────────────────────────────────
// Movimientos sueltos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Las operaciones de caja que una persona carga a mano.
 *
 * Primero se dice si la plata entra o sale, y despues se clasifica dentro de
 * ese lado: la lista de clasificaciones se filtra por `tipo`. Asi el signo lo
 * decide una sola pregunta explicita y no queda escondido adentro de una lista
 * mezclada, y sigue sin existir la combinacion imposible (un gasto que suma).
 */
export interface Operacion {
  clave: string;
  label: string;
  concepto:
    | "ALQUILER"
    | "SERVICIOS"
    | "SUELDO"
    | "IMPUESTO"
    | "COMISION"
    | "INSUMOS"
    | "VIATICOS"
    | "MANTENIMIENTO"
    | "RETIRO"
    | "DEVOLUCION_PRESTAMO"
    | "APORTE"
    | "PRESTAMO"
    | "INTERESES"
    | "OTRO";
  tipo: "INGRESO" | "EGRESO";
  ayuda: string;
  /** Va a nombre de un socio y solo se carga desde Socios (0089, 0108). */
  deSocio?: boolean;
}

/**
 * El orden es el de la lista: primero lo que cuesta tener el local abierto,
 * despues lo que no es gasto (devolver un prestamo) y "Otro" al final.
 *
 * (0155) Rehechas con lo que de verdad se cargo en noviembre: la limpieza, el
 * agua y la ferreteria terminaban como «Sueldo de socio», las comisiones del
 * banco y el pago a un proveedor como «Otro», y un sobrante como «Otro
 * ingreso». Cada ayuda dice ejemplos concretos, y el pago a proveedor ya no se
 * carga aca: desde Caja la plata salia sin bajar la deuda.
 */
export const OPERACIONES: ReadonlyArray<Operacion> = [
  {
    clave: "ALQUILER",
    label: "Alquiler y expensas",
    concepto: "ALQUILER",
    tipo: "EGRESO",
    ayuda: "Alquiler del local y expensas.",
  },
  {
    clave: "SERVICIOS",
    label: "Servicios",
    concepto: "SERVICIOS",
    tipo: "EGRESO",
    ayuda: "Luz, agua, gas, internet, teléfono, abonos de sistemas.",
  },
  {
    clave: "INSUMOS",
    label: "Limpieza, insumos y repuestos",
    concepto: "INSUMOS",
    tipo: "EGRESO",
    ayuda:
      "Limpieza (cloro, lavandina), ferretería, librería, cables, repuestos sueltos. Si el repuesto va cargado en una orden, su costo ya cuenta ahí.",
  },
  {
    clave: "VIATICOS",
    label: "Envíos, viáticos y comida",
    concepto: "VIATICOS",
    tipo: "EGRESO",
    ayuda: "Cadetería, flete, correo, nafta, viandas y comida del local.",
  },
  {
    clave: "MANTENIMIENTO",
    label: "Mantenimiento",
    concepto: "MANTENIMIENTO",
    tipo: "EGRESO",
    ayuda: "Arreglos del local, herramientas, equipamiento.",
  },
  {
    clave: "SUELDO",
    label: "Sueldos de empleados",
    concepto: "SUELDO",
    tipo: "EGRESO",
    ayuda:
      "A quien trabaja en el local y no es socio: sueldo, adelanto, comisión de venta. El sueldo de un socio se carga en Socios.",
  },
  {
    clave: "IMPUESTO",
    label: "Impuestos",
    concepto: "IMPUESTO",
    tipo: "EGRESO",
    ayuda: "Monotributo, ingresos brutos, tasas municipales.",
  },
  {
    clave: "COMISION",
    label: "Comisiones del banco",
    concepto: "COMISION",
    tipo: "EGRESO",
    ayuda:
      "Mantenimiento de cuenta, paquete del banco, comisiones de Mercado Pago o de la tarjeta.",
  },
  {
    clave: "DEVOLUCION_PRESTAMO",
    label: "Devolución de préstamo",
    concepto: "DEVOLUCION_PRESTAMO",
    tipo: "EGRESO",
    ayuda: "Devolver plata que se pidió prestada. No es un gasto: no resta de la ganancia.",
  },
  {
    clave: "OTRO_EGRESO",
    label: "Otro",
    concepto: "OTRO",
    tipo: "EGRESO",
    ayuda:
      "Sale plata por algo que no entra en las otras. Resta de la ganancia. Contá qué fue en el detalle.",
  },
  {
    clave: "RETIRO",
    label: "Retiro de socio",
    concepto: "RETIRO",
    tipo: "EGRESO",
    deSocio: true,
    ayuda:
      "Plata que se lleva un socio, o un gasto personal suyo pagado con plata del local. No es un gasto del negocio: va a su cuenta en Socios.",
  },
  {
    clave: "SUELDO_SOCIO",
    label: "Sueldo de socio",
    concepto: "SUELDO",
    tipo: "EGRESO",
    deSocio: true,
    ayuda:
      "Solo el sueldo pactado de un socio. Resta de la ganancia; en Socios se ve aparte. Lo que el socio paga para el local (limpieza, comida, ferretería) se carga en Caja como gasto.",
  },
  {
    clave: "APORTE",
    label: "Aporte de socio",
    concepto: "APORTE",
    tipo: "INGRESO",
    deSocio: true,
    ayuda: "Plata que pone un socio. No es una venta: descuenta de lo que lleva retirado.",
  },
  {
    clave: "PRESTAMO",
    label: "Préstamo recibido",
    concepto: "PRESTAMO",
    tipo: "INGRESO",
    ayuda: "Plata que entra y después hay que devolver. No es ganancia.",
  },
  {
    clave: "INTERESES",
    label: "Intereses y rendimientos",
    concepto: "INTERESES",
    tipo: "INGRESO",
    ayuda: "Lo que rinde la plata en Mercado Pago o en un plazo fijo. Suma a la ganancia.",
  },
  {
    clave: "OTRO_INGRESO",
    label: "Otro",
    concepto: "OTRO",
    tipo: "INGRESO",
    ayuda:
      "Entra plata que no es venta, cobro, aporte ni préstamo. Suma a la ganancia. Contá qué fue en el detalle.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Primero «qué fue», despues la categoria (0156)
// ─────────────────────────────────────────────────────────────────────────────

/** Lo que mas se carga a mano, como lo devuelve `GET /cajas/frecuentes`. */
export interface Frecuente {
  detalle: string;
  concepto: string;
  tipo: "INGRESO" | "EGRESO";
  veces: number;
}

/**
 * Palabras que dicen la categoria. Van primero: son las que se eligieron a
 * mano mirando lo que se cargo mal en noviembre (el agua y el cloro como
 * sueldo de socio, la comision del banco como «Otro»).
 */
const PALABRAS: ReadonlyArray<{ tipo: "INGRESO" | "EGRESO"; clave: string; patron: RegExp }> = [
  {
    tipo: "EGRESO",
    clave: "COMISION",
    patron: /comision|\bcomi\b|paquete|mantenimiento de cuenta/,
  },
  { tipo: "EGRESO", clave: "ALQUILER", patron: /alquiler|expensa/ },
  {
    tipo: "EGRESO",
    clave: "SERVICIOS",
    patron: /\bluz\b|\bagua\b|\bgas\b|internet|telefono|abono|edesur|edenor|epe\b/,
  },
  {
    tipo: "EGRESO",
    clave: "INSUMOS",
    patron: /cloro|lavandina|limpieza|ferreter|libreria|cable|repuesto|insumo|bolsa/,
  },
  {
    tipo: "EGRESO",
    clave: "VIATICOS",
    patron: /vianda|comida|almuerzo|cena|desayuno|nafta|flete|envio|correo|cadete|uber|taxi|remis/,
  },
  { tipo: "EGRESO", clave: "MANTENIMIENTO", patron: /arreglo|herramienta|pintura|mantenimiento/ },
  {
    tipo: "EGRESO",
    clave: "IMPUESTO",
    patron: /monotributo|ingresos brutos|\biibb\b|\bafip\b|\barca\b|\btasa\b|impuesto/,
  },
  { tipo: "EGRESO", clave: "SUELDO", patron: /sueldo|adelanto|empleado|aguinaldo/ },
  {
    tipo: "EGRESO",
    clave: "DEVOLUCION_PRESTAMO",
    patron: /devolucion.*prestamo|devuelvo.*prestamo/,
  },
  { tipo: "INGRESO", clave: "INTERESES", patron: /rendimiento|interes|plazo fijo/ },
  { tipo: "INGRESO", clave: "PRESTAMO", patron: /prestamo/ },
];

/**
 * La categoria que corresponde a lo que se escribio en «qué fue», o `null`.
 * Primero las palabras conocidas; si no, lo que se cargo antes con un detalle
 * parecido. Solo propone categorias de este lado y de este dialogo.
 */
export function proponerCategoria(
  detalle: string,
  tipo: "INGRESO" | "EGRESO",
  ambito: AmbitoMovimiento,
  frecuentes: ReadonlyArray<Frecuente> = [],
): string | null {
  const d = normalizar(detalle.trim());
  if (d.length < 3) return null;
  const posibles = new Set(operacionesDe(tipo, ambito).map((o) => o.clave));
  const porPalabra = PALABRAS.find((p) => p.tipo === tipo && p.patron.test(d));
  if (porPalabra && posibles.has(porPalabra.clave)) return porPalabra.clave;

  const palabras = new Set(d.split(/[^a-z0-9]+/).filter((w) => w.length >= 4));
  let mejor: { clave: string; veces: number } | null = null;
  for (const f of frecuentes) {
    if (f.tipo !== tipo) continue;
    const suyo = normalizar(f.detalle);
    const parecido =
      suyo === d || suyo.split(/[^a-z0-9]+/).some((w) => w.length >= 4 && palabras.has(w));
    const op = OPERACIONES.find((o) => o.concepto === f.concepto && o.tipo === tipo && !o.deSocio);
    if (parecido && op && posibles.has(op.clave) && (!mejor || f.veces > mejor.veces)) {
      mejor = { clave: op.clave, veces: f.veces };
    }
  }
  return mejor?.clave ?? null;
}

/** Un gasto que se repite, a un toque: lo escribe y elige la categoria (0156). */
export interface GastoFrecuente {
  etiqueta: string;
  detalle: string;
  clave: string;
}

const FRECUENTES_DE_SIEMPRE: ReadonlyArray<GastoFrecuente> = [
  { etiqueta: "Alquiler", detalle: "Alquiler", clave: "ALQUILER" },
  { etiqueta: "Luz", detalle: "Luz", clave: "SERVICIOS" },
  { etiqueta: "Agua", detalle: "Agua", clave: "SERVICIOS" },
  { etiqueta: "Internet", detalle: "Internet", clave: "SERVICIOS" },
  { etiqueta: "Vianda", detalle: "Vianda", clave: "VIATICOS" },
  { etiqueta: "Limpieza", detalle: "Limpieza", clave: "INSUMOS" },
  { etiqueta: "Comisión del banco", detalle: "Comisión del banco", clave: "COMISION" },
];

/**
 * Los botones de gastos frecuentes: los de siempre y, al final, hasta tres de
 * lo que mas se cargo (si se cargo tres veces o mas) que no esten ya.
 */
export function gastosFrecuentes(frecuentes: ReadonlyArray<Frecuente> = []): GastoFrecuente[] {
  const ya = new Set(FRECUENTES_DE_SIEMPRE.map((g) => normalizar(g.detalle)));
  const extras: GastoFrecuente[] = [];
  for (const f of frecuentes) {
    if (extras.length >= 3) break;
    if (f.tipo !== "EGRESO" || f.veces < 3) continue;
    const n = normalizar(f.detalle);
    const op = OPERACIONES.find(
      (o) => o.concepto === f.concepto && o.tipo === "EGRESO" && !o.deSocio,
    );
    if (!op || ya.has(n)) continue;
    ya.add(n);
    const etiqueta = f.detalle.charAt(0).toUpperCase() + f.detalle.slice(1);
    extras.push({ etiqueta, detalle: etiqueta, clave: op.clave });
  }
  return [...FRECUENTES_DE_SIEMPRE, ...extras];
}

/**
 * Un «Otro» grande pide confirmar (0156): el pago de $ 875.000 a LM Imports
 * entro como «Otro» y resto de la ganancia del mes.
 */
export const OTRO_GRANDE: Record<"ARS" | "USD", number> = { ARS: 50_000, USD: 50 };

export function esOtroGrande(
  op: Operacion | undefined,
  importe: number | null,
  moneda: "ARS" | "USD",
): boolean {
  return (
    op?.concepto === "OTRO" &&
    op.tipo === "EGRESO" &&
    importe !== null &&
    importe >= OTRO_GRANDE[moneda]
  );
}

/**
 * Lo que el detalle deja ver de una categoria mal elegida (0155). No frena:
 * avisa, y si hay una categoria que encaja, la ofrece. Sale de lo que se cargo
 * mal en noviembre: un pago a LM Imports como «Otro», la comision del banco
 * como «Otro», un sobrante a mano, la limpieza como sueldo de socio.
 */
export interface SugerenciaCategoria {
  texto: string;
  /** La `clave` de la operacion que encaja, si la hay en este mismo dialogo. */
  clave?: string;
}

const normalizar = (t: string): string =>
  t
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export function sugerirCategoria(
  detalle: string,
  op: Operacion | undefined,
  proveedores: ReadonlyArray<{ nombre: string }> = [],
): SugerenciaCategoria | null {
  const d = normalizar(detalle.trim());
  if (d.length < 3 || !op) return null;
  const sugerir = (clave: string, texto: string): SugerenciaCategoria | null =>
    op.clave === clave ? null : { texto, clave };

  if (op.tipo === "EGRESO" && op.concepto !== "RETIRO") {
    // Palabra por palabra y sin la «s» final: «VAPERS LM IMPORT LUCI» es un
    // pago a «LM Imports».
    const raiz = (w: string): string => w.replace(/s$/, "");
    const palabras = new Set(
      d
        .split(/[^a-z0-9]+/)
        .filter(Boolean)
        .map(raiz),
    );
    const prov = proveedores.find((p) => {
      const suyas = normalizar(p.nombre)
        .split(/[^a-z0-9]+/)
        .filter(Boolean)
        .map(raiz);
      return (
        suyas.length > 0 && suyas.some((w) => w.length >= 3) && suyas.every((w) => palabras.has(w))
      );
    });
    if (prov) {
      return {
        texto: `Parece un pago a ${prov.nombre}. Pagale desde Proveedores: ahí baja lo que se le debe y no cuenta como gasto.`,
      };
    }
  }
  if (/sobrante|faltante|diferencia de caja/.test(d)) {
    return {
      texto:
        "Para un sobrante o un faltante usá «Cerrar la caja»: contás lo que hay y la diferencia queda anotada sola.",
    };
  }
  if (op.tipo === "EGRESO" && /comision|\bcomi\b|paquete|mantenimiento de cuenta/.test(d)) {
    return sugerir(
      "COMISION",
      "Si es una comisión del banco o de Mercado Pago, va en «Comisiones del banco».",
    );
  }
  if (op.tipo === "INGRESO" && /rendimiento|interes/.test(d)) {
    return sugerir("INTERESES", "Si es lo que rindió la plata, va en «Intereses y rendimientos».");
  }
  if (op.concepto === "SUELDO" && /cloro|lavandina|limpieza|ferreter|libreria/.test(d)) {
    return {
      texto:
        "Eso parece un gasto del local, no un sueldo. Cargalo en Caja como «Limpieza, insumos y repuestos».",
    };
  }
  if (op.concepto === "SUELDO" && /\bagua\b|\bluz\b|internet|telefono/.test(d)) {
    return {
      texto: "Eso parece un servicio del local, no un sueldo. Cargalo en Caja como «Servicios».",
    };
  }
  if (op.concepto === "SUELDO" && /vianda|comida|almuerzo/.test(d)) {
    return {
      texto: "Si es comida para el local, cargala en Caja como «Envíos, viáticos y comida».",
    };
  }
  if (op.concepto === "OTRO" && op.tipo === "EGRESO" && /tarjeta|personal/.test(d)) {
    return {
      texto:
        "Si es un gasto personal de un socio, se puede cargar como retiro desde Socios. Si es del local, dejalo así.",
    };
  }
  return null;
}

export function buscarOperacion(clave: string): Operacion | undefined {
  return OPERACIONES.find((o) => o.clave === clave);
}

/**
 * Retiro, aporte y sueldo de socio van a nombre de un socio (0089, 0108). Se
 * mira la operacion y no el concepto: un SUELDO sin socio es el de un empleado.
 */
export function esDeSocio(op: Operacion | undefined): boolean {
  return op?.deSocio === true;
}

/**
 * Desde donde se carga. Los retiros y aportes de socios se cargan desde la
 * pantalla de Socios y solo desde ahi: en Caja eran dos categorias mas en una
 * lista que ya es larga, y la pregunta "¿de quien es la plata?" pertenece a la
 * pantalla donde se mira la cuenta de cada socio.
 */
export type AmbitoMovimiento = "caja" | "socios";

/** Las clasificaciones de un lado: lo que se ofrece despues de elegir si entra o sale. */
export function operacionesDe(
  tipo: Operacion["tipo"],
  ambito: AmbitoMovimiento = "caja",
): ReadonlyArray<Operacion> {
  return OPERACIONES.filter((o) => o.tipo === tipo && esDeSocio(o) === (ambito === "socios"));
}

// ─────────────────────────────────────────────────────────────────────────────
// Transferencias y cambio de divisa
// ─────────────────────────────────────────────────────────────────────────────

export interface EntradaCambio {
  monedaOrigen: Moneda;
  monedaDestino: Moneda;
  /** Lo que sale de la caja de origen. `null` si todavia no se cargo. */
  importeOrigen: number | null;
  /** Cotizacion REAL pactada en la operacion. `null` si todavia no se cargo. */
  cotizacion: number | null;
  /**
   * Si el cambio se cerro en un numero redondo, el destino se informa derecho y
   * la cotizacion pasa a ser una consecuencia, no un dato de entrada.
   */
  importeDestinoManual?: number | null;
  /** La cotizacion que tiene cargada el sistema. Sirve de referencia, no de dato. */
  cotizacionSistema?: number | null;
}

export interface CambioCalculado {
  /** Monedas distintas: no es mover plata, es comprar o vender dolares. */
  esCambioDivisa: boolean;
  /** Cuanto entra del otro lado. `null` cuando falta un dato para saberlo. */
  importeDestino: number | null;
  /** La cotizacion que termino rigiendo, se haya tipeado o deducido. */
  cotizacionEfectiva: number | null;
  /**
   * Diferencia en pesos contra la cotizacion del sistema. Positiva = el cambio
   * dejo ganancia; negativa = se pago mas caro que la referencia.
   */
  diferenciaArs: number | null;
  /** Que impide todavia calcular el destino. `null` si esta todo bien. */
  problema: string | null;
}

function esPositivo(n: number | null | undefined): n is number {
  return typeof n === "number" && Number.isFinite(n) && n > 0;
}

/**
 * Cuanto entra en la caja de destino.
 *
 * Tres casos:
 *  1. Misma moneda: entra lo mismo que sale. No hay cotizacion que valga.
 *  2. Monedas distintas con cotizacion: se convierte con la cotizacion PACTADA,
 *     no con la del sistema. La del sistema es una referencia; la que decide es
 *     la que se acordo en el mostrador.
 *  3. Monedas distintas con destino tipeado a mano: el destino manda y la
 *     cotizacion se deduce de los dos importes.
 */
export function calcularCambio(entrada: EntradaCambio): CambioCalculado {
  const { monedaOrigen, monedaDestino, importeOrigen, cotizacion, importeDestinoManual } = entrada;
  const esCambioDivisa = monedaOrigen !== monedaDestino;

  if (!esPositivo(importeOrigen)) {
    return {
      esCambioDivisa,
      importeDestino: null,
      cotizacionEfectiva: null,
      diferenciaArs: null,
      problema: "Falta el importe que sale.",
    };
  }

  if (!esCambioDivisa) {
    return {
      esCambioDivisa: false,
      importeDestino: redondear(importeOrigen),
      cotizacionEfectiva: null,
      diferenciaArs: null,
      problema: null,
    };
  }

  let importeDestino: number | null = null;
  let cotizacionEfectiva: number | null = null;

  if (esPositivo(importeDestinoManual)) {
    importeDestino = redondear(importeDestinoManual);
    // La cotizacion es siempre ARS por 1 USD, sin importar para que lado va la
    // operacion: los pesos arriba, los dolares abajo.
    const enArs = monedaOrigen === "ARS" ? importeOrigen : importeDestino;
    const enUsd = monedaOrigen === "USD" ? importeOrigen : importeDestino;
    cotizacionEfectiva = enUsd > 0 ? redondearCotizacion(enArs / enUsd) : null;
  } else if (esPositivo(cotizacion)) {
    cotizacionEfectiva = redondearCotizacion(cotizacion);
    importeDestino = convertir(importeOrigen, monedaOrigen, monedaDestino, cotizacionEfectiva);
  } else {
    return {
      esCambioDivisa: true,
      importeDestino: null,
      cotizacionEfectiva: null,
      diferenciaArs: null,
      problema: "Falta la cotizacion a la que se hizo el cambio.",
    };
  }

  // Cuanto se gano o se perdio contra la referencia del sistema. Es el mismo
  // calculo que hace `v_resultado_cambios` en la base.
  const referencia = entrada.cotizacionSistema;
  const diferenciaArs =
    esPositivo(referencia) && importeDestino !== null
      ? redondear(
          aArs(importeDestino, monedaDestino, referencia) -
            aArs(importeOrigen, monedaOrigen, referencia),
        )
      : null;

  return {
    esCambioDivisa: true,
    importeDestino,
    cotizacionEfectiva,
    diferenciaArs,
    problema: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Cheques
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Las cuentas que pueden recibir este cheque.
 *
 * El backend rechaza depositar un cheque en pesos en una cuenta en dolares
 * ("El cheque es en ARS y la cuenta Efectivo opera en USD"). Ofrecer esa
 * cuenta en el desplegable es ofrecer un error: la unica respuesta posible a
 * elegirla es un mensaje rojo. Se filtra antes.
 */
export function cuentasCompatibles<T extends { moneda: Moneda; activo?: boolean }>(
  cuentas: ReadonlyArray<T>,
  moneda: Moneda,
): T[] {
  return cuentas.filter((c) => c.moneda === moneda && c.activo !== false);
}

/**
 * Donde se puede depositar un cheque: un banco de su moneda.
 *
 * Antes se ofrecian todas las cuentas de la moneda y quedaba elegida la
 * primera, que es el cajon de billetes: confirmar sin mirar «depositaba» el
 * cheque en Efectivo o en Mercado Pago, adonde un cheque no entra.
 */
export function cuentasDeDeposito<T extends { moneda: Moneda; activo?: boolean; tipo?: string }>(
  cuentas: ReadonlyArray<T>,
  moneda: Moneda,
): T[] {
  return cuentasCompatibles(cuentas, moneda).filter((c) => c.tipo === "BANCO");
}

/** Los estados de cheque que la UI sabe nombrar. */
export type EstadoChequeConocido =
  "EN_CARTERA" | "DEPOSITADO" | "COBRADO" | "ACREDITADO" | "RECHAZADO" | "ENTREGADO" | "ANULADO";

export type TonoEstadoCheque = "info" | "warning" | "success" | "destructive" | "secondary";

/**
 * El unico mapa de estado de cheque (MASTER seccion 10: en cartera `info`,
 * depositado `warning`, cobrado `success`, rechazado `destructive`, entregado
 * `secondary` = terminal neutro). Toda pantalla pinta el estado con
 * `<Badge variant={TONO_ESTADO_CHEQUE[e]}>{ETIQUETA_ESTADO_CHEQUE[e]}</Badge>`.
 */
export const TONO_ESTADO_CHEQUE: Record<EstadoChequeConocido, TonoEstadoCheque> = {
  EN_CARTERA: "info",
  DEPOSITADO: "warning",
  COBRADO: "success",
  ACREDITADO: "success",
  RECHAZADO: "destructive",
  ENTREGADO: "secondary",
  // 0121_9b: el papel volvio con su venta anulada. Terminal, como entregado.
  ANULADO: "secondary",
};

export const ETIQUETA_ESTADO_CHEQUE: Record<EstadoChequeConocido, string> = {
  EN_CARTERA: "En cartera",
  DEPOSITADO: "Depositado",
  COBRADO: "Cobrado",
  ACREDITADO: "Acreditado",
  RECHAZADO: "Rechazado",
  ENTREGADO: "Entregado",
  ANULADO: "Anulado",
};

function esEstadoConocido(estado: string): estado is EstadoChequeConocido {
  return Object.prototype.hasOwnProperty.call(TONO_ESTADO_CHEQUE, estado);
}

/** Un estado que la UI no conoce no rompe: cae en `secondary` con su nombre crudo. */
export function tonoEstadoCheque(estado: string): TonoEstadoCheque {
  return esEstadoConocido(estado) ? TONO_ESTADO_CHEQUE[estado] : "secondary";
}

export function etiquetaEstadoCheque(estado: string): string {
  return esEstadoConocido(estado) ? ETIQUETA_ESTADO_CHEQUE[estado] : estado;
}

/** Solo un cheque EN_CARTERA se puede depositar: el resto ya se movio. */
export function sePuedeDepositar(estado: string): boolean {
  return estado === "EN_CARTERA";
}

/**
 * Un cheque ya rechazado no se rechaza de nuevo. Uno anulado con su venta
 * tampoco (0121_9b): la anulacion ya saco su plata y rechazarlo la sacaba otra
 * vez.
 */
export function sePuedeRechazar(estado: string): boolean {
  // (0155) Solo lo que todavia es nuestro: en cartera o depositado. Uno
  // endosado lo tiene el proveedor (la base lo frena), y lo rechazado o
  // anulado ya saco su plata.
  return estado === "EN_CARTERA" || estado === "DEPOSITADO";
}

/**
 * «Se acreditó» (0155): el cheque depositado, o el echeq que entro directo al
 * banco. El que esta en el cajon primero se deposita (la base lo frena igual).
 */
export function sePuedeAcreditar(estado: string): boolean {
  return estado === "DEPOSITADO";
}

/** Endosar a un proveedor (0155): solo el cheque que esta en cartera. */
export function sePuedeEndosar(estado: string): boolean {
  return estado === "EN_CARTERA";
}

/** Fecha a medianoche local: para comparar dias, la hora es ruido. */
function aDia(valor: string | Date): Date | null {
  const d = typeof valor === "string" ? new Date(valor) : valor;
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/**
 * Dias que faltan para poder cobrar el cheque. Negativo = ya vencio.
 * `null` si el cheque no tiene fecha de cobro cargada.
 */
export function diasHastaCobro(
  fechaCobro: string | null | undefined,
  hoy: Date = new Date(),
): number | null {
  if (!fechaCobro) return null;
  const cobro = aDia(fechaCobro);
  const dia = aDia(hoy);
  if (!cobro || !dia) return null;
  return Math.round((cobro.getTime() - dia.getTime()) / MS_POR_DIA);
}

export type Urgencia = "VENCIDO" | "POR_VENCER" | "AL_DIA" | "SIN_FECHA";

/** Cuantos dias antes del vencimiento el cheque ya pide atencion. */
export const DIAS_AVISO = 7;

/**
 * Que tan urgente es este cheque.
 *
 * Solo aplica a los que siguen EN CARTERA: un cheque ya depositado, cobrado o
 * entregado no "vence" para nosotros, porque ya salio de nuestras manos. Marcar
 * en rojo algo sobre lo que no hay nada que hacer entrena a ignorar el rojo.
 */
export function urgenciaCheque(
  cheque: { estado: string; fecha_cobro: string | null },
  hoy: Date = new Date(),
  diasAviso: number = DIAS_AVISO,
): { urgencia: Urgencia; dias: number | null } {
  const dias = diasHastaCobro(cheque.fecha_cobro, hoy);
  if (cheque.estado !== "EN_CARTERA") return { urgencia: "AL_DIA", dias };
  if (dias === null) return { urgencia: "SIN_FECHA", dias: null };
  if (dias < 0) return { urgencia: "VENCIDO", dias };
  if (dias <= diasAviso) return { urgencia: "POR_VENCER", dias };
  return { urgencia: "AL_DIA", dias };
}

/**
 * Por que se rechazo un cheque, sacado de sus observaciones.
 *
 * `rechazar_cheque` agrega «RECHAZADO: <motivo>» al final de lo que hubiera,
 * y hasta 0121 lo hacia con un « | » de mas adelante cuando no habia nada.
 * La lista de Rechazados no mostraba el motivo en ningun lado. `null` si no
 * hay nada que decir.
 */
export function motivoRechazo(observaciones: string | null | undefined): string | null {
  if (!observaciones) return null;
  const m = /RECHAZADO:\s*(.*)$/s.exec(observaciones);
  const motivo = (m ? m[1]! : observaciones).replace(/^[\s|]+/, "").trim();
  return motivo || null;
}

/**
 * La cartera partida por moneda.
 *
 * No hay un solo total: hay uno en pesos y otro en dolares. Sumarlos daria un
 * numero que no es plata de nada.
 */
export interface TotalesCartera {
  ars: number;
  usd: number;
  cantidadArs: number;
  cantidadUsd: number;
}

export function totalizarPorMoneda(
  cheques: ReadonlyArray<{ importe: Numerico; moneda: Moneda }>,
): TotalesCartera {
  return cheques.reduce<TotalesCartera>(
    (acc, ch) => {
      const monto = aNumero(ch.importe);
      if (ch.moneda === "USD") {
        acc.usd = redondear(acc.usd + monto);
        acc.cantidadUsd += 1;
      } else {
        acc.ars = redondear(acc.ars + monto);
        acc.cantidadArs += 1;
      }
      return acc;
    },
    { ars: 0, usd: 0, cantidadArs: 0, cantidadUsd: 0 },
  );
}

/**
 * La forma real que devuelve `v_cheques_cartera`, que trae mas columnas que la
 * tabla cruda: el nombre del cliente y los dias que faltan, ya calculados.
 */
export interface ChequeCartera extends Cheque {
  tipo?: string | null;
  librador?: string | null;
  cliente?: string | null;
  fecha_ingreso?: string | null;
  cuenta_deposito_id?: number | null;
  importe_ars?: Numerico | null;
  importe_usd?: Numerico | null;
}

/** Quien firmo el cheque, con el nombre que este cargado. */
export function firmanteCheque(ch: ChequeCartera): string {
  return ch.librador ?? ch.titular ?? ch.cliente ?? "Sin librador";
}

/**
 * Si un movimiento se puede anular desde Caja (0155): lo cargado a mano, que no
 * esta anulado ni es una anulacion. Es la misma regla que aplica la base en
 * `anular_movimiento_caja`; lo demas se deshace desde la operacion que lo
 * genero (la venta, el cobro, el pago, la transferencia, el conteo).
 */
const CONCEPTOS_MANUALES = new Set([
  "ALQUILER",
  "SERVICIOS",
  "SUELDO",
  "IMPUESTO",
  "COMISION",
  "INSUMOS",
  "VIATICOS",
  "MANTENIMIENTO",
  "RETIRO",
  "APORTE",
  "PRESTAMO",
  "DEVOLUCION_PRESTAMO",
  "INTERESES",
  "GASTO",
  "OTRO",
]);

export function sePuedeAnular(
  m: Pick<
    MovimientoCaja,
    | "concepto"
    | "comprobante_id"
    | "orden_id"
    | "movimiento_cc_id"
    | "movimiento_cp_id"
    | "transferencia_id"
    | "anula_id"
    | "anulado_at"
  >,
): boolean {
  return (
    CONCEPTOS_MANUALES.has(m.concepto) &&
    m.comprobante_id == null &&
    m.orden_id == null &&
    m.movimiento_cc_id == null &&
    m.movimiento_cp_id == null &&
    m.transferencia_id == null &&
    m.anula_id == null &&
    !m.anulado_at
  );
}

/** Etiqueta legible de cada concepto de movimiento. */
export const ETIQUETA_CONCEPTO: Record<string, string> = {
  COBRO: "Cobro",
  VENTA_CONTADO: "Venta contado",
  PAGO_PROVEEDOR: "Pago a proveedor",
  GASTO: "Gasto",
  SUELDO: "Sueldos",
  RETIRO: "Retiro de socio",
  APORTE: "Aporte de socio",
  TRANSFERENCIA: "Transferencia",
  CAMBIO_DIVISA: "Cambio de divisa",
  DEPOSITO_CHEQUE: "Depósito de cheque",
  IMPUESTO: "Impuestos",
  DEVOLUCION: "Devolución",
  VUELTO: "Vuelto",
  AJUSTE_ARQUEO: "Diferencia al cerrar la caja",
  PROPINA: "Propina",
  ALQUILER: "Alquiler y expensas",
  SERVICIOS: "Servicios",
  INSUMOS: "Limpieza, insumos y repuestos",
  VIATICOS: "Envíos, viáticos y comida",
  MANTENIMIENTO: "Mantenimiento",
  PRESTAMO: "Préstamo recibido",
  // 0155
  COMISION: "Comisiones del banco",
  DEVOLUCION_PRESTAMO: "Devolución de préstamo",
  INTERESES: "Intereses y rendimientos",
  // 0119: el egreso que deshace un cobro con cheque rebotado. Antes era OTRO
  // y restaba como gasto en Ganancias.
  RECHAZO_CHEQUE: "Cheque rechazado",
  OTRO: "Otro",
};

/** Etiqueta legible de cada tipo de cuenta financiera. */
export const ETIQUETA_TIPO_CUENTA: Record<string, string> = {
  CAJA: "Caja",
  BANCO: "Banco",
  BILLETERA: "Billetera",
  TARJETA: "Tarjeta",
  CRIPTO: "Cripto",
};

/** Nombre de la cuenta a partir de su id, para no mostrar numeros pelados. */
export function nombreCuenta(
  cuentas: ReadonlyArray<CuentaFinanciera | { id: number; nombre: string }>,
  id: number | null | undefined,
): string {
  if (id === null || id === undefined) return "—";
  return cuentas.find((c) => c.id === id)?.nombre ?? `Cuenta ${id}`;
}
